import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { LoginResponse, SessionUser } from "@octadiam/contracts";

export type AppEnvironment = "test" | "production";

type SessionState = {
  environment: AppEnvironment;
  accessToken: string | null;
  expiresAt: string | null;
  user: SessionUser | null;
  setEnvironment: (environment: AppEnvironment) => void;
  setSession: (session: LoginResponse) => void;
  setUser: (user: SessionUser) => void;
  clearSession: () => void;
};

export function hasCurrentSession(state = useSession.getState(), now = Date.now()): boolean {
  if (!state.accessToken || !state.user || !state.expiresAt) return false;
  const expiresAt = Date.parse(state.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt > now;
}

export function clearInvalidSession(now = Date.now()): boolean {
  const state = useSession.getState();
  if (hasCurrentSession(state, now)) return false;
  if (state.accessToken || state.expiresAt || state.user) state.clearSession();
  return true;
}

export function persistedSessionState(state: SessionState) {
  return { environment: state.environment };
}

export const useSession = create<SessionState>()(persist((set) => ({
  environment: "test",
  accessToken: null,
  expiresAt: null,
  user: null,
  setEnvironment: (environment) => set({
    environment,
    accessToken: null,
    expiresAt: null,
    user: null
  }),
  setSession: (session) => set({
    accessToken: session.accessToken,
    expiresAt: session.expiresAt,
    user: session.user
  }),
  setUser: (user) => set({ user }),
  clearSession: () => set({ accessToken: null, expiresAt: null, user: null })
}), {
  name: "octadiam-web-session",
  version: 2,
  migrate: (persisted) => {
    const state = (persisted || {}) as Partial<SessionState>;
    return {
      environment: state.environment === "production" ? "production" : "test",
      accessToken: null,
      expiresAt: null,
      user: null
    } as SessionState;
  },
  partialize: persistedSessionState
}));
